import React from 'react';

import { createRoot, type Root } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import classNames from 'classnames';

import { ClusterOptions, ExtendedMarkerData, IdType, TooltipElement } from '../../types';
import { getDataForMarker } from './markers-to-marker-data';
import { CLUSTER_ID } from '../hooks';
import { getPositionId } from '../hooks/use-clusters';
import { DEFAULT_GROUPED_CLUSTER_MIN_ZOOM, DEFAULT_MAX_ZOOM } from './utils';
import {
	buildGroupedClusterVisualPlan,
	type GroupedClusterSegment,
	type GroupedClusterVisualPlan
} from './grouped-cluster-layout';

import styles from './google-map-markers.module.scss';

type ReactRootContainer = HTMLElement & { __reactRoot?: Root };

const unmountClusterMarkerContent = (content: unknown) => {
	const container = content as ReactRootContainer | null | undefined;
	container?.__reactRoot?.unmount();
	delete container?.__reactRoot;
};

export type ClusterInstance = {
	clusterer: MarkerClusterer;
	dispose: () => void;
};

const getGroupedSegmentStyle = (
	segment: GroupedClusterSegment,
	plan: GroupedClusterVisualPlan
): React.CSSProperties => {
	if (segment.variant === 'single-route') {
		return { ['--segment-content-color' as string]: plan.shellBackground };
	}
	if (segment.variant === 'multi-driver') {
		return { ['--cluster-color' as string]: segment.driverRouteColor };
	}
	return {};
};

/**
 * Renders the grouped (max-zoom) cluster content into `container` **synchronously**
 * using `flushSync` so the container is never empty when Google Maps first places it
 * on the map.  With the old `createRoot().render()` approach the container would be
 * empty for one React scheduler tick because React 18 renders asynchronously.
 *
 * @param container  - The div that becomes the `AdvancedMarkerElement` content.
 * @param clusterMarkers - The individual markers that belong to this cluster.
 * @param position   - The cluster's geographic position.
 * @param tooltip    - Optional tooltip content.
 * @param onClick    - Optional click handler forwarded to the root div.
 */
export const renderGroupedClusterContent = (
	container: HTMLDivElement,
	clusterMarkers: google.maps.marker.AdvancedMarkerElement[],
	position: google.maps.LatLng,
	tooltip?: HTMLDivElement | string,
	onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
): void => {
	const markersCount = clusterMarkers.length;
	const markersData = clusterMarkers.map(marker =>
		getDataForMarker(marker as google.maps.marker.AdvancedMarkerElement)
	);
	const plan = buildGroupedClusterVisualPlan(markersData);

	// Unmount any previous root on this container before creating a new one.
	const prev = (container as ReactRootContainer).__reactRoot;
	if (prev) {
		prev.unmount();
		delete (container as ReactRootContainer).__reactRoot;
	}

	// flushSync forces React to complete the render synchronously so that
	// container.children is populated before this function returns.
	// This prevents a blank-frame gap when Google Maps places the marker.
	flushSync(() => {
		const root = createRoot(container);
		root.render(
			<div
				className={styles.clusterMaxZom}
				style={{
					['--grouped-cluster-shell-bg' as string]: plan.shellBackground,
					['--grouped-cluster-tip-color' as string]: plan.tipColor
				}}
				onClick={onClick}
				data-test-id={`cluster-max-zoom-${markersCount}`}
			>
				{plan.segments.map(segment => {
					const segmentStyle = getGroupedSegmentStyle(segment, plan);

					return (
						<div
							key={segment.key}
							className={classNames(styles.maxZoomIconTypeContainer, {
								[styles.maxZoomSegmentSingleRoute]: segment.variant === 'single-route',
								[styles.maxZoomSegmentMultiNeutral]: segment.variant === 'multi-neutral',
								[styles.maxZoomSegmentMultiDriver]: segment.variant === 'multi-driver'
							})}
							style={segmentStyle}
						>
							<span className={styles.maxZoomSegmentCount}>
								{segment.markers.length > 1 ? segment.markers.length : ''}
							</span>
							<BringgIcon iconName={segment.icon as BringgFontIcons} />
						</div>
					);
				})}
			</div>
		);
		(container as ReactRootContainer).__reactRoot = root;
	});
};

export const getCluster = (
	map: google.maps.Map,
	markerInstanceMap: Map<IdType, google.maps.marker.AdvancedMarkerElement>,
	infoWindow: google.maps.InfoWindow,
	clusterOptions: ClusterOptions
): ClusterInstance => {
	const markers: google.maps.marker.AdvancedMarkerElement[] = [];
	const trackedClusterMarkers = new Set<google.maps.marker.AdvancedMarkerElement>();

	const cleanupClusterMarkerRoots = () => {
		trackedClusterMarkers.forEach(marker => {
			unmountClusterMarkerContent(marker.content);
			marker.map = null;
		});
		trackedClusterMarkers.clear();
	};

	markerInstanceMap.forEach(markerInstance => {
		const data = getDataForMarker(markerInstance);
		if (data && data.shouldCluster) {
			markerInstance.map = null;
			markers.push(markerInstance);
		}
	});

	const onClusterClick = (
		event: any,
		position: google.maps.LatLng,
		clusterMarkers: google.maps.marker.AdvancedMarkerElement[],
		tooltip?: HTMLDivElement | string
	) => {
		event.stopPropagation();
		if (clusterOptions.onClusterClick) {
			const markerData = clusterMarkers.map(marker =>
				getDataForMarker(marker as google.maps.marker.AdvancedMarkerElement)
			);
			clusterOptions.onClusterClick?.(event, markerData as ExtendedMarkerData[]);
		}

		const ctrlKey = event.metaKey || event.ctrlKey;
		if (clusterOptions.getClusterTooltip && !ctrlKey) {
			openClusterInfoWindow({
				markers: clusterMarkers,
				position,
				infoWindow,
				map,
				clusterOptions,
				tooltip
			});
		} else if (ctrlKey) {
			infoWindow.set(CLUSTER_ID, null);
			infoWindow.close();
		}
	};

	const renderGroupedCluster = (
		container: HTMLDivElement,
		clusterMarkers: google.maps.marker.AdvancedMarkerElement[],
		position: google.maps.LatLng,
		tooltip?: HTMLDivElement | string
	) => {
		renderGroupedClusterContent(container, clusterMarkers, position, tooltip, e =>
			onClusterClick(e, position, clusterMarkers, tooltip)
		);
	};

	const getMarker = (
		markersCount: number,
		clusterMarkers: google.maps.marker.AdvancedMarkerElement[],
		position: google.maps.LatLng
	) => {
		const container = document.createElement('div');
		if (!clusterMarkers || !clusterMarkers.length) {
			return container;
		}
		const markersData = clusterMarkers.map(marker =>
			getDataForMarker(marker as google.maps.marker.AdvancedMarkerElement)
		);

		const zoom = map.getZoom?.();
		const groupedClusterMinZoom = clusterOptions.groupedClusterMinZoom ?? DEFAULT_GROUPED_CLUSTER_MIN_ZOOM;
		const isGroupedClusterZoom = typeof zoom === 'number' ? zoom >= groupedClusterMinZoom : false;

		const allSameColor = markersData.every(marker => marker?.color && marker.color === markersData[0]?.color);
		const color = allSameColor ? markersData[0]?.color : 'black';

		let tooltip: HTMLDivElement | string | undefined;
		if (clusterOptions.getClusterTooltip) {
			tooltip = clusterOptions.getClusterTooltip(
				clusterMarkers
					.map((marker: google.maps.marker.AdvancedMarkerElement) => getDataForMarker(marker))
					.filter(Boolean) as ExtendedMarkerData[]
			);
		}
		if (isGroupedClusterZoom) {
			renderGroupedCluster(container, clusterMarkers, position, tooltip);
		} else {
			const root = createRoot(container);
			root.render(
				<div
					className={styles.cluster}
					style={{ ['--cluster-color' as string]: color }}
					onClick={e => onClusterClick(e, position, clusterMarkers, tooltip)}
					data-test-id={`cluster-${markersCount}`}
				>
					<span className={styles.markerText} style={{ ['--cluster-color' as string]: color }}>
						{markersCount}
					</span>
				</div>
			);
			(container as ReactRootContainer).__reactRoot = root;
		}

		return container;
	};

	const clusterer = new MarkerClusterer({
		map,
		markers,
		algorithm: new SuperClusterAlgorithm({ maxZoom: DEFAULT_MAX_ZOOM + 1 }),
		renderer: {
			render: ({ count, position, markers: clusterMarkers }) => {
				const clusterMarker = new google.maps.marker.AdvancedMarkerElement({
					map,
					position,
					content: getMarker(count, clusterMarkers as google.maps.marker.AdvancedMarkerElement[], position)
				});
				trackedClusterMarkers.add(clusterMarker);
				return clusterMarker;
			}
		}
	});

	const dispose = () => {
		cleanupClusterMarkerRoots();
	};

	return { clusterer, dispose };
};

export const openClusterInfoWindow = ({
	markers,
	position,
	infoWindow,
	map,
	clusterOptions,
	tooltip
}: {
	markers: google.maps.marker.AdvancedMarkerElement[];
	position: google.maps.LatLng;
	infoWindow: google.maps.InfoWindow;
	map: google.maps.Map;
	clusterOptions: ClusterOptions;
	id?: string;
	tooltip?: HTMLDivElement | string;
}) => {
	if (clusterOptions.getClusterTooltip) {
		const markersData = markers?.map(marker => getDataForMarker(marker));

		if (markersData.length) {
			const clusterId = getPositionId(position);
			infoWindow.set(CLUSTER_ID, clusterId);
			infoWindow.setPosition({ lat: position.lat(), lng: position.lng() });

			// Center the info window above the cluster
			infoWindow.setOptions({
				pixelOffset: new google.maps.Size(0, -26)
			});

			(tooltip as TooltipElement)?.renderTooltip?.();

			infoWindow.setContent(tooltip);
			infoWindow.open(map);
		}
	}
};
